import React, {
  createContext,
  useContext,
  useRef,
  useState,
} from "react";
import Annotator from "../utils/annotator/Annotator";
import AnnotationStorageProvider from "../utils/annotator/AnnotationStorageProvider";
import AnnotationApiClient from "../utils/annotator/AnnotationApiClient";
import AddHighlightPopover from "../components/AddHighlightPopover";

const AnnotationsContext = createContext(null);

export function AnnotationsProvider({ bookshareId, isRnib, children }) {
  const annotatorRef = useRef(null);
  if (!annotatorRef.current) {
    const annotator = new Annotator(parseInt(bookshareId));
    annotator.storageProvider = new AnnotationStorageProvider();
    annotator.apiClient = isRnib ? null : new AnnotationApiClient();
    annotatorRef.current = annotator;
  }

  const [bookmarks, setBookmarks] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [showHighlightPopover, setShowHighlightPopover] = useState(null);

  async function initializeAnnotations() {
    const [bookmarks, highlights] = await Promise.all([
      annotatorRef.current.initializeBookmarks(),
      annotatorRef.current.initializeHighlights(),
    ]);
    setBookmarks(bookmarks);
    setHighlights(highlights);
  }

  function addBookmark(cfi, text, locator) {
    annotatorRef.current.addBookmark(cfi, text, locator);
    setBookmarks(annotatorRef.current.listActiveBookmarks());
  }

  function removeBookmark(location) {
    if (annotatorRef.current.removeBookmark(location)) {
      setBookmarks(annotatorRef.current.listActiveBookmarks());
    }
  }

  function addHighlight(location, text, color, note, locator) {
    annotatorRef.current.addHighlight(location, text, color, note, locator);
    setHighlights(annotatorRef.current.listActiveHighlights());
  }

  function removeHighlight(location) {
    if (annotatorRef.current.removeHighlight(location)) {
      setHighlights(annotatorRef.current.listActiveHighlights());
    }
  }

  function createHighlight(
    cfi,
    text,
    locator,
    selectionRect,
    getViewportRect,
    onDismiss,
  ) {
    setShowHighlightPopover({
      cfi,
      text,
      locator,
      selectionRect,
      getViewportRect,
      onDismiss,
    });
  }

  function handleHighlightSave(note) {
    const { cfi, text, locator, onDismiss } = showHighlightPopover;
    addHighlight(cfi, text, "#FFFF00", note, locator);
    setShowHighlightPopover(null);
    onDismiss?.();
  }

  function handleHighlightCancel() {
    const { onDismiss } = showHighlightPopover;
    setShowHighlightPopover(null);
    onDismiss?.();
  }

  return (
    <AnnotationsContext.Provider
      value={{
        bookmarks,
        highlights,
        removeBookmark,
        removeHighlight,
        initializeAnnotations,
        addBookmark,
        addHighlight,
        createHighlight,
        isHighlightPopoverOpen: () => showHighlightPopover !== null,
      }}
    >
      {children}
      {showHighlightPopover && (
        <AddHighlightPopover
          onSave={handleHighlightSave}
          onCancel={handleHighlightCancel}
          selectionRect={showHighlightPopover.selectionRect}
          getViewportRect={showHighlightPopover.getViewportRect}
          text={showHighlightPopover.text}
        />
      )}
    </AnnotationsContext.Provider>
  );
}

export function useAnnotations() {
  return useContext(AnnotationsContext);
}

export default AnnotationsContext;
